import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Users, ChevronDown, ChevronUp, Gift, Send, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * CashierDigestPreviewPanel — collapsible admin card that previews which
 * cashier will get what in the next Monday rebate digest. Auto-hides
 * entirely when nothing is queued for the week, so it never adds noise
 * on quiet weeks.
 *
 * Data source: `GET /api/admin/cashout-fees/cashier-digest-preview`.
 * Refreshes every 90s to stay current as new fee-cashouts land.
 */
const fmt$ = (n) => `$${Number(n || 0).toFixed(2)}`;

const CashierDigestPreviewPanel = () => {
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(false);
    const [sending, setSending] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await axios.get(
                `${API}/admin/cashout-fees/cashier-digest-preview`,
                { withCredentials: true },
            );
            setRows(Array.isArray(data?.cashiers) ? data.cashiers : []);
            setTotal(Number(data?.total) || 0);
        } catch {
            /* silent — not critical */
        } finally {
            setLoading(false);
        }
    }, []);

    // "Send now" fires the weekly rebate cron immediately. Grants players
    // their rebates + fires the cashier digests. `force=1` bypasses the
    // once-per-week guard so admins can preview the exact copy live during
    // testing. Warns before firing since it moves real money.
    const sendNow = useCallback(async (e) => {
        e?.stopPropagation?.();
        const ok = window.confirm(
            'Send this Monday\'s digest right now?\n\n' +
            'This will grant Free Play rebates to every eligible player AND ' +
            'push the digest to each cashier. Cannot be undone.',
        );
        if (!ok) return;
        setSending(true);
        try {
            const { data } = await axios.post(
                `${API}/admin/cashout-fees/run-rebate?force=1`,
                {}, { withCredentials: true },
            );
            toast.success(
                `Digest sent — $${Number(data?.granted || 0).toFixed(2)} to ` +
                `${data?.players || 0} player${data?.players === 1 ? '' : 's'} · ` +
                `${data?.cashier_digests_sent || 0} cashier push${data?.cashier_digests_sent === 1 ? '' : 'es'}`,
                { duration: 6000 },
            );
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Send failed');
        } finally {
            setSending(false);
        }
    }, [load]);

    useEffect(() => {
        load();
        const id = setInterval(load, 90_000);
        return () => clearInterval(id);
    }, [load]);

    if (loading) return null;
    if (rows.length === 0) return null;

    return (
        <div
            className="rounded-2xl border border-[#00FFFF]/30 bg-gradient-to-br from-[#001a1f] via-[#0e0e0e] to-[#0e0e0e] p-4"
            data-testid="home-cashier-digest-panel"
        >
            <div
                role="button"
                tabIndex={0}
                onClick={() => setExpanded(v => !v)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpanded(v => !v); } }}
                className="w-full flex items-center justify-between gap-3 text-left cursor-pointer"
                data-testid="home-cashier-digest-toggle"
            >
                <div className="flex items-center gap-2">
                    <Gift className="w-4 h-4 text-[#00FFFF]" />
                    <p className="text-xs uppercase tracking-wider text-[#A0AAB2] font-bold">
                        Monday digest preview
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); sendNow(); }}
                        disabled={sending}
                        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10.5px] font-bold border border-[#00FFFF]/60 text-[#00FFFF] hover:bg-[#00FFFF]/10 transition disabled:opacity-50"
                        data-testid="home-cashier-digest-send-now"
                        title="Force-run the rebate cron now — grants Free Play + fires the cashier digest push"
                    >
                        {sending
                            ? <Loader2 className="w-3 h-3 animate-spin" />
                            : <Send className="w-3 h-3" />}
                        {sending ? 'Sending…' : 'Send now'}
                    </button>
                    <span className="text-[11px] text-[#A0AAB2]">
                        <span className="text-white font-bold tabular-nums" data-testid="home-cashier-digest-total">
                            {fmt$(total)}
                        </span>
                        {' '}across {rows.length} cashier{rows.length === 1 ? '' : 's'}
                    </span>
                    {expanded
                        ? <ChevronUp className="w-4 h-4 text-[#A0AAB2]" />
                        : <ChevronDown className="w-4 h-4 text-[#A0AAB2]" />}
                </div>
            </div>

            {expanded && (
                <div className="mt-3 pt-3 border-t border-white/10 space-y-2" data-testid="home-cashier-digest-rows">
                    {rows.map((r) => (
                        <div
                            key={r.cashier_id}
                            className="flex items-start justify-between gap-3 rounded-lg bg-black/30 px-3 py-2 border border-white/5"
                        >
                            <div className="flex items-start gap-2 min-w-0">
                                <Users className="w-3.5 h-3.5 text-[#00FFFF] mt-1 shrink-0" />
                                <div className="min-w-0">
                                    <p className="text-white text-sm font-bold truncate" title={r.cashier_name}>
                                        {r.cashier_name}
                                    </p>
                                    {r.top_players?.length > 0 && (
                                        <p className="text-[10.5px] text-[#A0AAB2] truncate">
                                            {r.top_players.slice(0, 3).map(p => `${p.name} (${fmt$(p.rebate)})`).join(' · ')}
                                            {r.player_count > 3 && ` + ${r.player_count - 3} more`}
                                        </p>
                                    )}
                                </div>
                            </div>
                            <div className="text-right shrink-0">
                                <p className="text-[#00FFFF] font-black text-base tabular-nums">{fmt$(r.total)}</p>
                                <p className="text-[9.5px] text-[#A0AAB2] uppercase tracking-wide">
                                    {r.player_count} player{r.player_count === 1 ? '' : 's'}
                                </p>
                            </div>
                        </div>
                    ))}
                    <p className="text-[10.5px] text-[#A0AAB2] italic pt-1">
                        Sent to each cashier as a push + in-app notification when the
                        rebate cron fires Monday at 9 AM CT.
                    </p>
                </div>
            )}
        </div>
    );
};

export default CashierDigestPreviewPanel;
